import { useFlightStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  type AlgomerchTag,
  CoreFlightsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { AlgomerchTags, FlightTime } from "@tenant/flights";
import { AirlineIcon, Icon } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./FlightCardSummary.module.scss";

export interface FlightCardSummaryComponentProps
  extends PlatformComponentProps {
  duration: string;
  currentPriceText: string;
  departureTime: string;
  departureMilitaryTime?: string;
  arrivalTime: string;
  arrivalMilitaryTime?: string;
  brandName: string;
  primaryCarrier: string;
  airlineName: string;
  additionalAirlines?: number;
  onClickTag?: (label: string) => void;
  tags: AlgomerchTag[];
  tagsLimit?: number;
  rewardText?: string;
  flightStops: number;
  displayPrice?: boolean;
}

export const FlightCardSummary = ({
  duration,
  currentPriceText,
  departureTime,
  departureMilitaryTime,
  arrivalTime,
  arrivalMilitaryTime,
  brandName,
  primaryCarrier,
  airlineName,
  additionalAirlines,
  tags,
  tagsLimit,
  flightStops,
  className,
  children,
  displayPrice = true,
}: FlightCardSummaryComponentProps) => {
  departureTime = departureMilitaryTime ? departureMilitaryTime : departureTime;
  arrivalTime = arrivalMilitaryTime ? arrivalMilitaryTime : arrivalTime;
  const { t } = useI18nContext();

  const styles = useFlightStyles(
    CoreFlightsComponent.FlightCardSummary,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(
    styles,
    CoreFlightsComponent.FlightCardSummary
  );
  return (
    <div className={clsx(block, className)}>
      <div className={cn("icon")}>
        <AirlineIcon
          airlineCode={primaryCarrier}
          altText={airlineName}
          className={styles["airline__icon"]}
        />
      </div>
      {tags.length > 0 && (
        <div className={cn("flight-tags")}>
          <AlgomerchTags tags={tags} tagsLimit={tagsLimit} />
        </div>
      )}
      <FlightTime
        departureTime={departureTime}
        arrivalTime={arrivalTime}
        stops={flightStops}
      />

      {displayPrice && <div className={cn("amount")}>{currentPriceText}</div>}
      <div className={cn("details")}>
        <div className={cn("icon")}>
          <AirlineIcon
            airlineCode={primaryCarrier}
            altText={airlineName}
            className={styles["airline__icon"]}
          />
        </div>
        <div className={cn("flight-details")}>
          {airlineName}
          <div className={cn("duration")}>{duration}</div>
        </div>
        <div className={cn("airline-details")}>
          {(additionalAirlines ?? 0) > 0 ? (
            <div className={cn("airline")}>
              <span className={cn("additional-airlines")}>
                {" "}
                {t("core-flights.vi.countOther", { count: additionalAirlines })}
              </span>
            </div>
          ) : null}
          <div className={cn("brand-name")}>{brandName}</div>
          <div className={cn("stops")}>
            {flightStops === 0
              ? t("stopDetails.nonstop")
              : t("stopDetails.stop", { count: flightStops })}
          </div>
        </div>
      </div>
      {children}
    </div>
  );
};
